import { LogIn, Dog, LogOut, ShowerHead, BedDouble, Check, Triangle, X } from "lucide-react";
import { Shell, PageHead, Section } from "./dc";

const TILES = [
  { k: "arrivals", icon: LogIn, label: "Still to check in", value: 7, status: "warn", foot: "31 of 38 checked in", b: { large: 3, small: 2, pp: 1 }, d: { large: 1, small: 0, pp: 0 } },
  { k: "inbuilding", icon: Dog, label: "Checked in now", value: 64, status: "pos", foot: "In the building right now · tap for list", b: { large: 12, small: 8, pp: 2 }, d: { large: 24, small: 15, pp: 3 } },
  { k: "departures", icon: LogOut, label: "Going home now", value: 22, foot: "9 of 31 gone home", b: { large: 4, small: 2, pp: 0 }, d: { large: 10, small: 5, pp: 1 } },
  { k: "baths", icon: ShowerHead, label: "Baths", value: 11, status: "neg", foot: "4 behind schedule", b: { large: 3, small: 1, pp: 0 }, d: { large: 4, small: 3, pp: 0 } },
  { k: "overnight", icon: BedDouble, label: "Overnight", value: 41, b: { large: 22, small: 15, pp: 4 } },
];

const STATUS = {
  pos: { Icon: Check, text: "On track", cls: "bg-[#E7F4EC] text-[#2F8F5F] border-[#2F8F5F]/30" },
  warn: { Icon: Triangle, text: "At risk", cls: "bg-[#FBF1D9] text-[#9A6B00] border-[#9A6B00]/30" },
  neg: { Icon: X, text: "Behind", cls: "bg-[#FBE7E4] text-[#C2483A] border-[#C2483A]/30" },
};

const VARIANTS = {
  subtle: {
    title: "A · Subtle", sub: "White → light teal. Closest to today, adds gentle depth.",
    card: "bg-gradient-to-br from-white via-[#F5F9F9] to-[#DCEEEB] border-[#1F8A7C]/20 hover:border-[#1F8A7C]/45",
    label: "text-[#5B7480] [&_svg]:text-[#1F8A7C]", num: "text-[#13212A]", divider: "border-[#1F8A7C]/15",
    foot: "text-[#5B7480]", pill: "bg-white/80 text-[#13212A] border border-[#1F8A7C]/20", grp: "text-[#5B7480]",
  },
  medium: {
    title: "B · Medium", sub: "Light teal → teal tint. Tiles read as a clear brand band.",
    card: "bg-gradient-to-br from-[#E3F1EF] via-[#C9E5E0] to-[#A6D3CB] border-[#1F8A7C]/30 hover:border-[#1F8A7C]/60",
    label: "text-[#145E55] [&_svg]:text-[#145E55]", num: "text-[#0E3F39]", divider: "border-[#145E55]/20",
    foot: "text-[#145E55]", pill: "bg-white/70 text-[#0E3F39] border border-white/60", grp: "text-[#145E55]",
  },
  bold: {
    title: "C · Bold", sub: "Deep teal with white text. Maximum emphasis, hero-style.",
    card: "bg-gradient-to-br from-[#1F8A7C] via-[#187368] to-[#0F4F48] border-[#0F4F48] hover:brightness-110 shadow-[0_6px_18px_-8px_rgba(15,79,72,0.6)]",
    label: "text-white/75 [&_svg]:text-white/80", num: "text-white", divider: "border-white/20",
    foot: "text-white/80", pill: "bg-white/15 text-white border border-white/25", grp: "text-white/70",
    badge: "bg-white text-[#13212A] border-white",
  },
};

const StatusBadge = ({ s, v, testid }) => {
  const { Icon, text, cls } = STATUS[s];
  const ink = { pos: "#2F8F5F", warn: "#9A6B00", neg: "#C2483A" }[s];
  return (
    <span className={`ml-auto inline-flex items-center gap-1 text-[10px] font-bold normal-case tracking-normal px-1.5 py-0.5 rounded-full border ${v.badge || cls}`} data-testid={testid}>
      <Icon size={10} strokeWidth={3} style={{ color: ink }} />{text}
    </span>
  );
};

const Group = ({ label, d, v }) => (
  <div className="flex flex-wrap items-center gap-1">
    <span className={`text-[11px] font-bold w-full ${v.grp}`}>{label} <span className="font-mono">{d.large + d.small + d.pp}</span></span>
    {[["Lg", d.large], ["Sm", d.small], ["PP", d.pp]].map(([l, n]) => (
      <span key={l} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${v.pill}`}>{l} <span className="font-mono">{n}</span></span>
    ))}
  </div>
);

const Tile = ({ t, v, vk }) => {
  const Icon = t.icon;
  return (
    <div className={`border rounded-xl px-4 py-3 min-w-0 transition-[border-color,filter,transform] duration-200 hover:-translate-y-0.5 ${v.card}`} data-testid={`kpi-${vk}-${t.k}`}>
      <div className={`text-[11px] font-bold uppercase tracking-wide flex items-center gap-1.5 ${v.label}`}>
        <Icon className="h-3.5 w-3.5" /> {t.label}
        {t.status && <StatusBadge s={t.status} v={v} testid={`kpi-${vk}-${t.k}-status`} />}
      </div>
      <div className={`font-mono text-[28px] font-bold leading-tight mt-0.5 ${v.num}`}>{t.value}</div>
      <div className={`mt-2 pt-2 border-t space-y-1 ${v.divider}`}>
        <Group label="Board" d={t.b} v={v} />
        {t.d && <Group label="Daycare" d={t.d} v={v} />}
      </div>
      {t.foot && <div className={`text-[11px] font-semibold mt-2 ${v.foot}`}>{t.foot}</div>}
    </div>
  );
};

const Row = ({ vk, delay }) => {
  const v = VARIANTS[vk];
  return (
    <Section title={v.title} sub={v.sub} icon={Dog} delay={delay} testid={`kpi-variant-${vk}`} style={{ marginTop: 24 }}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {TILES.map((t) => <Tile key={t.k} t={t} v={v} vk={vk} />)}
      </div>
    </Section>
  );
};

export default function KpiGradientMockup() {
  return (
    <Shell title="Daily Ops · KPI gradients" testid="kpi-gradient-mockup">
      <PageHead title="Today's View · teal gradient tiles" sub="Three gradient intensities for the Daily Ops KPI tiles. Sample data only — the live app is unchanged. Status badges (✓ / △ / ✗) stay small per the design spec." />
      {["subtle", "medium", "bold"].map((k, i) => <Row key={k} vk={k} delay={i * 80} />)}
    </Shell>
  );
}
